import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { companyDetailOptions } from '../../models/companies/queries';
import { useDealsList } from '../../models/deals/queries';

/**
 * Per-company extras the list endpoint does not return: deal stats (from one deals fetch) and
 * contacts (from the batch-fetched details of the visible page, which also pre-caches the drawer).
 * Companies whose details are still loading are absent from the contact maps.
 *
 * @param {Array<Object>} companies - The visible page
 * @returns {{dealStatsByCompany: Map, contactCountByCompany: Map, contactsByCompany: Map}}
 */
export function useCompanyRowDetails(companies) {
	const { data: dealsData } = useDealsList({ pageSize: 500 });
	const allDeals = useMemo(() => dealsData?.deals ?? [], [dealsData?.deals]);

	const dealStatsByCompany = useMemo(() => {
		const map = new Map();
		for (const d of allDeals) {
			const key = d.crmCompanyId;
			if (!key) continue;
			if (!map.has(key)) map.set(key, { dealCount: 0, openCount: 0, totalValue: 0 });
			const s = map.get(key);
			s.dealCount += 1;
			if (d.status === 'open') s.openCount += 1;
			s.totalValue += Number(d.dealValue ?? 0);
		}
		return map;
	}, [allDeals]);

	const detailQueries = useQueries({
		queries: companies.map((c) => companyDetailOptions(c.id)),
	});

	const contactsByCompany = useMemo(() => {
		const map = new Map();
		companies.forEach((c, i) => {
			const contacts = detailQueries[i]?.data?.contacts;
			if (contacts) map.set(c.id, contacts);
		});
		return map;
	}, [companies, detailQueries]);

	const contactCountByCompany = useMemo(
		() => new Map([...contactsByCompany].map(([id, contacts]) => [id, contacts.length])),
		[contactsByCompany],
	);

	return { dealStatsByCompany, contactCountByCompany, contactsByCompany };
}
